import React from 'react';
import * as maplibregl from 'maplibre-gl';
import { AlertFilled, ApiFilled, BellFilled, EyeFilled, KeyOutlined } from '@ant-design/icons';
import { isEmpty } from 'underscore';
import { Link } from 'react-router-dom';
import { Tooltip } from 'antd';
import history from './history';
import MapPopups from '../../components/MapOld/elements/MapPopups';
import { openStreetView } from './functions';
import { UnitOfMeasurement, UnitOfSpeed, AssetMarkerData, AssetMarkerPoint, EventIconType, Geofence, HeatmapEvent, MapMode, SnailTrailPoint } from '../../types/types';
import type { Feature, FeatureCollection, GeoJsonProperties, Geometry, Point } from 'geojson';
import { FlagFilled, LocationFilled, NotificationFilled, PlugFilled } from '@carbon/icons-react';
import { createGoogleStyle } from 'maplibre-google-maps';
import constants from '../constants';

const {
    MapAssetPopupHead,
    MapAssetPopupContent,
} = MapPopups;

const mapHeatMap = (mapDeviceHeatMap) => {
    const heatMap = {
        positions: mapDeviceHeatMap ? mapDeviceHeatMap.map((mapDeviceHeatMapItem) => ({
            lat: parseFloat(mapDeviceHeatMapItem.latitude),
            lng: parseFloat(mapDeviceHeatMapItem.longitude),
            weight: parseInt(mapDeviceHeatMapItem.weight, 10) * 10,
        })) : [],
        options: {
            radius: 10,
            opacity: 0.6,
        },
    };

    return heatMap;
};

const mapHeatMapMarkers = (mapDeviceHeatMap, googleMapAccess) => {
    const mapBounds = googleMapAccess && googleMapAccess.current && googleMapAccess.current.state && googleMapAccess.current.state.map && googleMapAccess.current.state.map.getBounds();

    let northEastLat = 0;
    let northEastLng = 0;
    let getSouthWestLat = 0;
    let getSouthWestLng = 0;

    if (mapBounds && mapBounds.getNorthEast && mapBounds.getSouthWest) {
        northEastLat = mapBounds.getNorthEast().lat();
        northEastLng = mapBounds.getNorthEast().lng();

        getSouthWestLat = mapBounds.getSouthWest().lat();
        getSouthWestLng = mapBounds.getSouthWest().lng();
    }

    const localMarkers = mapDeviceHeatMap.filter((item) => {
        const lat = parseFloat(item.latitude);
        const lng = parseFloat(item.longitude);
        const minLat = getSouthWestLat;
        const maxLat = northEastLat;
        const minLng = getSouthWestLng;
        const maxLng = northEastLng;
        return lat > minLat && lat < maxLat && lng > minLng && lng < maxLng;
    });

    const total = localMarkers.length;
    const max = 50;
    const step = Math.max(Math.floor(total / max), 1);
    const decimated = localMarkers.filter((item, i) => i % step === 0);

    let markers = [];
    if (!isEmpty(decimated)) {
        markers = decimated.map((item, key) => {
            const data = item.events;
            const content = data.map((dataPoint) => (
                <div key={`heat-map-item-${key}`}>
                    <Link to={`${`/events/custom-view/${dataPoint.event_id}`}/${dataPoint.device_id}`}>
                        {dataPoint.date}: {dataPoint.asset_name}<div style={{ marginLeft: '40px' }}> {dataPoint.event_title}</div>
                    </Link>
                </div>
            ));

            let EventIcon = AlertFilled;
            switch (data[0].event_icon) {
            case 'key':
                EventIcon = KeyOutlined;
                break;
            case 'eye':
                EventIcon = EyeFilled;
                break;
            case 'api':
                EventIcon = ApiFilled;
                break;
            case 'thunderbolt':
                EventIcon = BellFilled;
                break;
            default:
                EventIcon = AlertFilled;
            }

            return {
                title: (
                    <MapAssetPopupHead
                        heading={item.title}
                        registration={item.registration} />
                ),
                lat: parseFloat(item.latitude),
                lng: parseFloat(item.longitude),
                record_id: item.record_id,
                is_event: true,
                onClick: () => history.push(`${`/events/custom-view/${data[0].event_id}`}/${data[0].device_id}`),
                content,
                forceInfoBox: item.events.length > 1,
                icon: <Tooltip><EventIcon className="event-icon" /></Tooltip>,
            };
        });
    }

    return markers;
};

const setMarkerPopoverToLoading = (ref) => {
    let { markerPopover } = ref.state;
    if (markerPopover) {
        const updatedMarkerPopover = React.cloneElement(markerPopover, { showLoading: true });
        ref.setState({ markerPopover: updatedMarkerPopover });
    } else {
        markerPopover = (
            <MapAssetPopupContent
                showLoading />
        );
        ref.setState({ markerPopover });
    }
};

const requestMarkerDetails = (marker, ref) => {
    if (marker && marker.record_id) {
        const { actions } = ref.props;
        const event = marker.is_event;
        const payload = { record_id: marker.record_id };

        if (event) {
            payload.event = true;
        }

        actions.getDeviceLocationDetailedInfoRequest(payload);
    } else if (marker && marker.date_from) {
        const { actions } = ref.props;
        const { is_event, date_from, asset_id } = marker;
        const payload = { asset_id, date_to: date_from };

        if (is_event) {
            payload.event = true;
        }

        actions.getDeviceLocationDetailedInfoRequest(payload);
    } else if (ref) {
        const markerPopover = (
            <MapAssetPopupContent
                showNoData />
        );
        ref.setState({ markerPopover });
    }
};

const setMarkerPopoverDetails = (ref, live = false, uomPref = null) => {
    const { deviceDetailedLocationInfo, user, company } = ref.props;
    const { marker } = deviceDetailedLocationInfo || {};
    if (!isEmpty(marker)) {
        let { speed } = marker || {};
        speed = Math.floor(speed);
        let speedUnit = uomPref === UnitOfMeasurement.BritishImperial ? UnitOfSpeed.BritishImperial : UnitOfSpeed.Metric;
        const event_key = deviceDetailedLocationInfo.event ? marker.event_key : null;
        const event_type = deviceDetailedLocationInfo.event ? marker.event_type : null;
        const dateTime = deviceDetailedLocationInfo.event ? marker.date : null;
        const markerPopover = (
            <MapAssetPopupContent
                showLoading={false}
                online={deviceDetailedLocationInfo.online}
                online_status={deviceDetailedLocationInfo.online_status}
                event_key={event_key}
                event_type={event_type}
                dateTime={dateTime}
                driver={deviceDetailedLocationInfo.driver}
                assetPicture={deviceDetailedLocationInfo.asset_picture}
                userPicture={deviceDetailedLocationInfo.user_picture}
                defaultImage={user?.userCompany?.company_image}
                deviceModel={deviceDetailedLocationInfo.device_model}
                location_data={deviceDetailedLocationInfo.marker?.data || []}
                showPicture
                speed={speed}
                live={live}
                speedUnit={speedUnit}
                streetViewIconClicked={openStreetView}
                googleMapAccessRef={ref.googleMapAccess}
                lat={marker.lat}
                lng={marker.lng}
                angle={marker.angle} 
                showTimeToDestinationBtn={company && company.features && company.features.find((feature) => feature.id === 'time-to-destination')} />
        );

        ref.setState({ markerPopover });
    } else {
        const markerPopover = (
            <MapAssetPopupContent
                showNoData />
        );

        ref.setState({ markerPopover });
    }
};

const getMapAssetPopupHead = (detailedInfo) => (
    <MapAssetPopupHead
        heading={detailedInfo.title}
        registration={detailedInfo.registration} />
);

const getMapAssetPopupContent = (ref, detailedInfo, speed, unit, defaultImage = null) => {
    const { company } = ref.props;
    return (
        <MapAssetPopupContent
            showLoading={false}
            online={0}
            online_status={detailedInfo.online_status}
            event_key={detailedInfo.marker?.event_key}
            event_type={detailedInfo.marker?.event_type}
            driver={detailedInfo.driver}
            assetPicture={detailedInfo.asset_picture}
            userPicture={detailedInfo.user_picture}
            defaultImage={defaultImage}
            deviceModel={detailedInfo.device_model}
            location_data={detailedInfo.marker?.data || []}
            showPicture
            speed={speed}
            speedUnit={unit}
            streetViewIconClicked={openStreetView}
            googleMapAccessRef={ref.googleMapAccess}
            lat={detailedInfo.lat}
            lng={detailedInfo.lng}
            angle={detailedInfo.angle} 
            showTimeToDestinationBtn={company && company.features && company.features.find((feature) => feature.id === 'time-to-destination')} />
    );
};

const calculateSpeedAndUnit = (detailedInfo, user) => {
    let { speed } = detailedInfo;
    let unit = UnitOfSpeed.Metric;

    if (user && user.profile && user.profile.unit_of_measurement_preference === UnitOfMeasurement.BritishImperial) {
        unit = UnitOfSpeed.BritishImperial;
        speed = Math.floor(detailedInfo.speed * 0.62);
    }

    return {
        speed,
        unit,
    };
};




























/* NEW MAP UTILS */

/**
 * Build a style specification to set on the map or build the map from
 * @param satelliteModeActive whether to enable satellite tiles
 * @returns a MapLibre style specification
 */
const getMapStyle = (satelliteModeActive: boolean): maplibregl.StyleSpecification => {
    const STYLE_TYPE = 'google' as const;
    const MODE_SATELLITE = 'satellite' as const;
    const MODE_ROADMAP = 'roadmap' as const;
    const MAPLIBRE_FONT_URL = 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf' as const;
    const s = createGoogleStyle(
        STYLE_TYPE,
        satelliteModeActive ? MODE_SATELLITE : MODE_ROADMAP,
        constants.GOOGLE_MAP_API_KEY
    ) as maplibregl.StyleSpecification;
    (s as any).glyphs = MAPLIBRE_FONT_URL;
    return s;
};

/**
 * Transforms an array of AssetMarkerData into a GeoJSON FeatureCollection for consumption by MapLibre.
 * @param data an array of AssetMarkerData to build from
 * @param sliderValue the current value selected by the user using the slider, used to compute where to display marker in time
 */
const createFeatureCollectionFromAssetMarkerData = (
    data: AssetMarkerData[],
    sliderValue: number,
    mapMode: MapMode
): FeatureCollection<Point, GeoJsonProperties> => ({
    type: 'FeatureCollection',
    features: data
        .filter(({ lng, lat }) => isValidLngLat(lng, lat))
        .map((assetMarkerData): Feature<Point, GeoJsonProperties> | null => {
            const DEFAULT_ANGLE = 45;
            const firstMarkerPoint: AssetMarkerPoint = assetMarkerData.markers[0];
            const lastMarkerPoint: AssetMarkerPoint = assetMarkerData.markers[assetMarkerData.markers.length - 1];
            let angle = firstMarkerPoint?.angle || DEFAULT_ANGLE;
            const division_color = assetMarkerData.division_color;
            const device_status = assetMarkerData.device_status;
            let arrow_color = getDeviceStatusColor((device_status || 0), firstMarkerPoint?.is_idle || false);
            const sort_index = Number(assetMarkerData.asset_id) || 0;
            let dvrImei = "";
            if (assetMarkerData.dvr_devices?.length) {
                dvrImei = assetMarkerData.dvr_devices[0].imei;
            }
            let lastRecordId = 0;
            if (assetMarkerData.markers?.length) {
                lastRecordId = assetMarkerData.markers[0].record_id;
            }
            let lng = assetMarkerData.lng;
            let lat = assetMarkerData.lat;
            if (mapMode === MapMode.Live) {
                lng = firstMarkerPoint.lng;
                lat = firstMarkerPoint.lat;
            } else {
                let closestMarkerPoint: AssetMarkerPoint = lastMarkerPoint;
                let closestMarkerFound: boolean = false;
                assetMarkerData.markers.forEach((marker) => {
                    if (closestMarkerFound) {
                        return;
                    }
                    if (marker.time_interval > sliderValue) {
                        closestMarkerPoint = marker;
                        closestMarkerFound = true;
                    }
                });
                lng = Number(closestMarkerPoint.lng);
                lat = Number(closestMarkerPoint.lat);
                angle = Number(closestMarkerPoint.angle);
                arrow_color = getDeviceStatusColor(device_status, closestMarkerPoint.is_idle || false);
            }
            if (!isValidLngLat(lng, lat)) {
                return null;
            }
            const angleBucket = snapDegreesToBuckets(angle);
            const icon_id = createIconId(division_color, arrow_color, angleBucket);
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [lng, lat]
                },
                properties: {
                    ...assetMarkerData,
                    angle: angleBucket,
                    arrow_color,
                    icon_id,
                    sort_index,
                    dvr_imei: dvrImei,
                    last_record_id: lastRecordId
                } as GeoJsonProperties
            };
        })
        .filter((assetMarkerData) => assetMarkerData !== null)
});

/**
 * Transforms an array of SnailTrailPoint into a GeoJSON FeatureCollection for consumption by MapLibre.
 * Renders a path.
 * @param points SnailTrailPoint[]
 * @returns GeoJSON
 */
const createFeatureCollectionFromSnailTrailPoints = (points: SnailTrailPoint[]): GeoJSON.GeoJSON<Geometry, GeoJsonProperties> => ({
    type: 'FeatureCollection',
    features: points && points.length
        ? [{
            type: 'Feature',
            geometry: {
                type: 'LineString',
                coordinates: points.map(p => [parseFloat(p.lng), parseFloat(p.lat)] as [number, number])
            },
            properties: {}
        }]
        : []
});

/**
 * Transforms an array of Geofence into a feature collection for consumption by MapLibre.
 * @param geofences an array of Geofence to build the geojson from
 */
const createFeatureCollectionFromGeofences = (geofences: Geofence[] | null): FeatureCollection => {
    const features: GeoJSON.Feature<GeoJSON.Polygon, GeoJsonProperties>[] = [];
    if (!Array.isArray(geofences)) {
        return {
            type: 'FeatureCollection',
            features
        };
    }
    for (const g of geofences) {
        const ring: [number, number][] = [];
        for (const tp of g.trigger_points || []) {
            const lng = Number(tp?.lng);
            const lat = Number(tp?.lat);
            if (isValidLngLat(lng, lat)) {
                ring.push([lng, lat]);
            }
        }
        if (ring.length >= 3) {
            features.push({
                type: 'Feature',
                geometry: {
                    type: 'Polygon',
                    coordinates: [ring]
                },
                properties: {
                    id: g.id,
                    name: g.name
                }
            });
        }
    }
    return {
        type: 'FeatureCollection',
        features
    };
};

/**
 * Transforms an array of HeatmapEvent into a feature collection for consumption by MapLibre.
 * @param data an array of HeatmapEvent to build geojson from
 */
const createFeatureCollectionFromHeatmapEvents = (data: HeatmapEvent[] | null): FeatureCollection<Point, any> => {
    const items = Array.isArray(data) ? data : [];
    if (!items.length) {
        return {
            type: 'FeatureCollection',
            features: []
        };
    }
    const valid = items.filter(d =>
        Number.isFinite(d.latitude) &&
        Number.isFinite(d.longitude) &&
        Number.isFinite(d.weight)
    );
    if (!valid.length) {
        return {
            type: 'FeatureCollection',
            features: []
        };
    }
    const weights = valid.map(d => d.weight);
    const minW = Math.min(...weights);
    const maxW = Math.max(...weights);
    const range = Math.max(1e-9, maxW - minW);
    return {
        type: 'FeatureCollection',
        features: valid.map(d => {
            const asset_id = d.events?.length ? d.events[0].device_id : 0;
            const record_id = d.record_id;
            return {
                type: 'Feature',
                geometry: {
                    type: 'Point',
                    coordinates: [d.longitude, d.latitude]
                },
                properties: {
                    w: (d.weight - minW) / range,
                    weight: d.weight,
                    asset_id,
                    record_id
                }
            };
        })
    };
};

/**
 * Build a FeatureCollection of point centers from the geofence polygons.
 * Uses a simple average of the outer ring coordinates – good enough for
 * a visual marker at national / regional zoom levels.
 */
const createGeofenceCentersFromGeojson = (
    geofenceGeojson: FeatureCollection
): FeatureCollection<Point, GeoJsonProperties> => {
    const out: Feature<Point, GeoJsonProperties>[] = [];

    for (const f of geofenceGeojson.features as Feature[]) {
        if (!f || !f.geometry) continue;

        let ring: [number, number][] | null = null;

        if (f.geometry.type === 'Polygon') {
            const coords = f.geometry.coordinates as [number, number][][];
            ring = coords[0] || null;
        } else if (f.geometry.type === 'MultiPolygon') {
            const coords = f.geometry.coordinates as [number, number][][][];
            ring = coords[0]?.[0] || null;
        } else {
            continue;
        }

        if (!ring || ring.length === 0) continue;

        let sumLng = 0;
        let sumLat = 0;
        let count = 0;

        for (const [lng, lat] of ring) {
            if (Number.isFinite(lng) && Number.isFinite(lat)) {
                sumLng += lng;
                sumLat += lat;
                count += 1;
            }
        }

        if (!count) continue;

        const center: [number, number] = [sumLng / count, sumLat / count];

        out.push({
            type: 'Feature',
            geometry: {
                type: 'Point',
                coordinates: center,
            },
            properties: {
                ...(f.properties || {}),
            },
        });
    }

    return {
        type: 'FeatureCollection',
        features: out,
    };
};

/**
 * Wraps functions to prevent them from running before all styles are loaded, use when interacting with the map itself
 * @param map the current prop of your map ref
 * @param fn the function to run when ready, interact with the map here
 */
const safelyInteractWithMap = (
    map: maplibregl.Map,
    fn: () => void
): void => {
    if (map.isStyleLoaded()) {
        fn();
    } else {
        map.once('idle', fn);
    }
};

/**
 * Ensures lat/lng bounds are finite, non-falsy and logical
 * @param lng longitude
 * @param lat latitude
 * @returns
 */
const isValidLngLat = (
    lng: unknown,
    lat: unknown
): boolean => {
    if (!Number.isFinite(lng)) {
        return false;
    }
    if (!Number.isFinite(lat)) {
        return false;
    }
    if (Math.abs(lng as number) > 180) {
        return false;
    }
    if (Math.abs(lat as number) > 90) {
        return false;
    }
    if ((lng as number) === 0 && (lat as number) === 0) {
        return false;
    }
    return true;
};

/**
 * Merge incoming asset markers into an existing array of asset markers.
 * Allows us to display full list in sidebar and perform instant map mode switches.
 * @param existing the existing array of AssetMarkerData
 * @param incoming the incoming array of AssetMarkerData
 */
const mergeAssetMarkers = (
    existing: AssetMarkerData[],
    incoming: AssetMarkerData[]
): AssetMarkerData[] => {
    const merged = new Map<number, AssetMarkerData>();
    for (const marker of existing) {
        merged.set(marker.asset_id, marker);
    }
    for (const incomingMarker of incoming) {
        const existingMarker = merged.get(incomingMarker.asset_id);
        if (existingMarker) {
            const newMarkers = incomingMarker.markers?.length ? incomingMarker.markers : existingMarker.markers;
            merged.set(incomingMarker.asset_id, {
                ...existingMarker,
                ...incomingMarker,
                markers: newMarkers
            });
        } else {
            merged.set(incomingMarker.asset_id, incomingMarker);
        }
    }
    return Array.from(merged.values());
};

/**
 * Filter an array of SnailTrailPoint to return only points that have a valid event and a valid lat/lng
 * @param markers your points to filter
 */
export function filterSnailTrailPointsForEvents(points: SnailTrailPoint[] | null): SnailTrailPoint[] {
    if (!points?.length) {
        return [];
    }
    return points.filter((m) =>
        typeof m.event_type === 'number' &&
        m.event_type > 0 &&
        isValidLngLat(parseFloat(m.lng), parseFloat(m.lat)
    ));
}

/**
 * Returns a lng/lat tuple from a SnailTrailPoint, checks for validity, returns null if invalid
 * @param point your SnailTrailPoint
 */
const getLngLatFromSnailTrailPoint = (point: SnailTrailPoint): [number, number] | null => {
    const lat = parseFloat(point.lat);
    const lng = parseFloat(point.lng);
    if (isValidLngLat(lng, lat)) {
        return [lng, lat];
    }
    return null;
}

/**
 * Resolve css var to hex value we can use in places where css vars are unsupported.
 * For example var(--safety-orange) -> #ff6900
 * @param cssVarName
 */
const resolveCssVar = (cssVarName: string): string => {
    let out = (cssVarName || '').trim();
    if (!out) {
        return out;
    }
    const root = document.documentElement;
    const styles = getComputedStyle(root);
    for (let i = 0; i < 5 && /^var\(/.test(out); i++) {
        const m = out.match(/^var\((--[A-Za-z0-9\-_]+)(?:,\s*([^)]+))?\)\s*$/);
        if (!m) {
            break;
        }
        const [, varName, fallback] = m;
        const value = styles.getPropertyValue(varName).trim();
        out = value || (fallback ? fallback.trim() : out);
    }
    return out;
};

/**
 * Convert color to hex6
 * @param color the color to convert
 */
const toHex6 = (color: string): string => {
    const HEX_FALLBACK = '#888888' as const;
    const resolved = resolveCssVar(color);
    const ctx = document.createElement('canvas').getContext('2d')!;
    ctx.fillStyle = '#000';
    ctx.fillStyle = resolved;
    const parsed = ctx.fillStyle as string;
    if (parsed.startsWith('#')) {
        if (parsed.length === 4) {
            return '#' + [...parsed.slice(1)].map(ch => ch + ch).join('');
        }
        return parsed.slice(0, 7).toLowerCase();
    }
    const m = parsed.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i);
    if (m) {
        const [r, g, b] = m.slice(1, 4).map(n => Math.max(0, Math.min(255, parseInt(n, 10))));
        const to2 = (n: number) => n.toString(16).padStart(2, '0');
        return `#${to2(r)}${to2(g)}${to2(b)}`;
    }
    return HEX_FALLBACK;
};

/**
 * Construct a unique icon id for the given colors and angle
 * @param divisionColor the color of the division
 * @param arrowColor the color of the asset arrow
 * @param angleBucket the angle bucket (0-350 in steps of 10)
 */
const createIconId = (
    divisionColor: string,
    arrowColor: string,
    angleBucket: number
) => {
    const PREFIX = 'asset-' as const;
    return `${PREFIX}${toHex6(divisionColor)}-${toHex6(arrowColor)}-a${angleBucket}`;
};

/**
 * Get directional arrow spacing by zoom level
 * @param zoom your zoom level of your map
 */
const getSnailTrailDirectionalArrowSpacingForZoom = (zoom: number) => {
    if (zoom < 9) {
        return 260;
    }
    if (zoom < 12) {
        return 200;
    }
    if (zoom < 14) {
        return 160;
    }
    if (zoom < 16) {
        return 120;
    }
    if (zoom < 18) {
        return 100;
    }
    return 80;
};

/**
 * Snap degrees to nearest angle by bucket size. Reduces memory usage.
 * @param degrees the degrees to snap
 * @param bucketSize the bucket size to snap to, e.g. nearest 10 degrees
 */
const snapDegreesToBuckets = (
    degrees: number,
    bucketSize = 10
): number => {
    const s = Math.max(1, Math.abs(Math.trunc(bucketSize)));
    let d = Number.isFinite(degrees) ? degrees % 360 : 0;
    if (d < 0) {
        d += 360;
    }
    return (Math.round(d / s) * s) % 360;
};

/**
 * Get the color associated with a device status
 * @param deviceStatus numeric representation of device.status
 * @param isIdle boolean if asset is idle
 */
const getDeviceStatusColor = (
    deviceStatus: number,
    isIdle: boolean
): string => {
    const RED = 'var(--red-dark)' as const;
    const YELLOW = 'var(--yellow-dark)' as const;
    const GREEN = 'var(--green-dark)' as const;
    if (Number(deviceStatus) === 0) {
        return RED;
    }
    if (isIdle) {
        return YELLOW;
    }
    return GREEN;
};

/**
 * Check if a mouse hit is within an icon's draw area
 * @param dx 
 * @param dy 
 * @param iconSize 
 * @param iconId 
 * @param metaMap 
 * @param bmpMap 
 */
const isInsideSpriteAlpha = (
    dx: number,
    dy: number,
    iconSize: number,
    iconId: string,
    metaMap: Map<string, { w: number; h: number; pixelRatio: number }>,
    bmpMap: Map<string, { w: number; h: number; pixelRatio: number; data: Uint8ClampedArray }>
): boolean => {
    const ALPHA_MIN = 16 as const;
    const bmp = bmpMap.get(iconId);
    if (bmp) {
        const { w, h, pixelRatio, data } = bmp;
        const px = Math.floor(w / 2 + dx * (pixelRatio / iconSize));
        const py = Math.floor(h / 2 + dy * (pixelRatio / iconSize));
        if (px < 0 || py < 0 || px >= w || py >= h) {
            return false;
        }
        const a = data[(py * w + px) * 4 + 3];
        return a > ALPHA_MIN;
    }
    const meta = metaMap.get(iconId);
    const rCss = meta ? (Math.min(meta.w, meta.h) / meta.pixelRatio) * 0.5 * iconSize - 1 : 14 * iconSize;
    return dx * dx + dy * dy <= rCss * rCss;
};

/**
 * Helper to find the right GPU marker to hover based on z-order
 * @param map the current value of your map ref
 * @param mouse the x,y of the mouse
 */
const pickTopmostHitAlpha = (
    map: maplibregl.Map,
    mouse: { x: number; y: number },
    assetMarkersLayerId: string
): maplibregl.MapGeoJSONFeature | undefined => {
    const ICON_QUERY_PAD_PX = 20 as const;
    if (!map) {
        return;
    }
    const iconSize = Number(map.getLayoutProperty(assetMarkersLayerId, 'icon-size') as any) || 1;
    const pad = Math.ceil(ICON_QUERY_PAD_PX * iconSize);
    const box: [maplibregl.PointLike, maplibregl.PointLike] = [
        [mouse.x - pad, mouse.y - pad],
        [mouse.x + pad, mouse.y + pad]
    ];
    const hits = map.queryRenderedFeatures(box, { layers: [assetMarkersLayerId] }) as maplibregl.MapGeoJSONFeature[];
    if (!hits.length) {
        return;
    }
    const metaMap: Map<string, { w: number; h: number; pixelRatio: number }> = (map as any).__assetIconMeta || new Map();
    const bmpMap: Map<string, { w: number; h: number; pixelRatio: number; data: Uint8ClampedArray }> = (map as any).__assetIconBitmap || new Map();
    for (const f of hits) {
        const iconId = (f.properties as any)?.icon_id as string | undefined;
        if (!iconId) {
            continue;
        }
        const [lng, lat] = (f.geometry as GeoJSON.Point).coordinates as [number, number];
        const c = map.project({ lng, lat });
        const dx = mouse.x - c.x;
        const dy = mouse.y - c.y;
        if (isInsideSpriteAlpha(dx, dy, iconSize, iconId, metaMap, bmpMap)) {
            return f;
        }
    }
};

/**
 * Draw a rounded rectangle path
 * @param ctx context
 * @param x x
 * @param y y
 * @param w width
 * @param h height
 * @param r radius
 */
function roundedRectPath(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    r: number
) {
    const r2 = Math.min(r, h / 2, w / 2);
    ctx.beginPath();
    ctx.moveTo(x + r2, y);
    ctx.arcTo(x + w, y, x + w, y + h, r2);
    ctx.arcTo(x + w, y + h, x, y + h, r2);
    ctx.arcTo(x, y + h, x, y, r2);
    ctx.arcTo(x, y, x + w, y, r2);
    ctx.closePath();
}

/**
 * Convert any number to a non-negative integer. Rounds to the nearest integer and clamps negatives to 0.
 * @param value the value to convert
 */
export function toNonNegativeInt(value: number): number {
    if (!Number.isFinite(value)) {
        return 0;
    }
    const rounded = Math.round(value);
    if (rounded < 0) {
        return 0;
    }
    return rounded;
}

/**
 * 
 * @param s 
 * @returns 
 */
const hashString = (string: string) => {
    let h = 5381;
    for (let i = 0; i < string.length; i++) {
        h = ((h << 5) + h) ^ string.charCodeAt(i);
    }
    return (h >>> 0).toString(36);
};

/**
 * Calculate the bounds from a geofence collection
 * @param geofenceGeojson feature collection containing geofence data
 */
const getBoundsForGeojson = (geofenceGeojson: GeoJSON.FeatureCollection): [[number, number],[number, number]] | null => {
    let minLng = Infinity;
    let minLat = Infinity;
    let maxLng = -Infinity;
    let maxLat = -Infinity;
    const update = (lng: number, lat: number) => {
        if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
            return;
        }
        if (lng < minLng) {
            minLng = lng;
        }
        if (lat < minLat) {
            minLat = lat;
        }
        if (lng > maxLng) {
            maxLng = lng;
        }
        if (lat > maxLat) {
            maxLat = lat;
        }
    };
    for (const f of geofenceGeojson.features) {
        const g = f.geometry;
        if (!g) {
            continue;
        }
        if (g.type === 'Polygon') {
            for (const ring of g.coordinates) {
                for (const [lng, lat] of ring) {
                    update(lng, lat);
                }
            }
        } else if (g.type === 'MultiPolygon') {
            for (const poly of g.coordinates) {
                for (const ring of poly) {
                    for (const [lng, lat] of ring) {
                        update(lng, lat);
                    }
                }
            }
        } else if (g.type === 'LineString') {
            for (const [lng, lat] of g.coordinates) {
                update(lng, lat);
            }
        } else if (g.type === 'Point') {
            const [lng, lat] = g.coordinates; update(lng, lat);
        }
    }
    if (minLng === Infinity) {
        return null;
    }
    return [[minLng, minLat], [maxLng, maxLat]];
};

/**
 * Prewarm asset markers images to avoid flicker on load on large accounts
 * @param map the current value of your map ref
 * @param assetMarkersGeojson asset markers geojson data
 * @param assetNamesActive whether asset names is active or not
 */
export const prewarmAssetMarkerIcons = (
    map: maplibregl.Map,
    assetMarkersGeojson: FeatureCollection<Point, GeoJsonProperties>,
    assetNamesActive: boolean
) => {
    if (!map || !assetMarkersGeojson?.features?.length) {
        return;
    }
    for (const f of assetMarkersGeojson.features as Feature<Point, any>[]) {
        const p = f.properties || {};
        if (!p?.division_color || !p?.arrow_color) {
            continue;
        }
        const angle = Number(p.angle) || 0;
        const label = assetNamesActive ? (p.asset_name || p.name || p.label || p.registration || '') : '';
        loadAssetMarker(map, p.division_color, p.arrow_color, angle, false);
        if (assetNamesActive || label) {
            loadAssetMarker(map, p.division_color, p.arrow_color, angle, true, 0, String(label));
        }
    }
};

/**
 * Loads a marker image into the map
 * @param map the current value of your map ref
 * @param divisionColor the color of the division for this asset
 * @param arrowColor the color of the arrow for this asset
 * @param angleDeg the angle in degrees for this asset
 */
const loadAssetMarker = (
    map: maplibregl.Map,
    divisionColor: string,
    arrowColor: string,
    angleDeg: number,
    usePill: boolean = false,
    pillGrowRight: number = 0,
    label: string = ''
): string => {
    const TEXT_HASH_PREFIX = '__t' as const;
    const PILL_SUFFIX_PREFIX = '__pill__w' as const;
    const HEX_WHITE = '#ffffff' as const;
    const PIXEL_RATIO = 2 as const;
    const CANVAS_SIZE = 64 as const;
    const STROKE_JOIN_ROUND = 'round' as const;
    const STROKE_CAP_ROUND = 'round' as const;
    const angleBucket = snapDegreesToBuckets(angleDeg);
    const baseId = createIconId(divisionColor, arrowColor, angleBucket);
    const labelKey = label ? `${TEXT_HASH_PREFIX}${hashString(label)}` : '';
    const pillId = `${baseId}${PILL_SUFFIX_PREFIX}${toNonNegativeInt(pillGrowRight)}${labelKey}`;
    /* eslint-disable no-multi-assign */
    const cache: Set<string> = ((map as any).__assetIconCache ||= new Set());
    /* eslint-enable no-multi-assign */
    const W = CANVAS_SIZE * PIXEL_RATIO;
    const H = CANVAS_SIZE * PIXEL_RATIO;
    const addImageWithMeta = (
        id: string,
        draw: (ctx: CanvasRenderingContext2D) => ImageData | { width: number; height: number; data: Uint8ClampedArray }
    ) => {
        if ((map as any).hasImage?.(id)) {
            cache.add(id);
            return;
        }
        const canvas = document.createElement('canvas');
        canvas.width = W;
        canvas.height = H;
        const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
        ctx.imageSmoothingEnabled = true;
        ctx.scale(PIXEL_RATIO, PIXEL_RATIO);
        ctx.translate(CANVAS_SIZE / 2, CANVAS_SIZE / 2);
        ctx.lineJoin = STROKE_JOIN_ROUND;
        ctx.lineCap = STROKE_CAP_ROUND;
        const maybeImg = draw(ctx);
        let imgData: Uint8ClampedArray;
        let imgWidth: number;
        let imgHeight: number;
        if ('data' in maybeImg && 'width' in maybeImg && 'height' in maybeImg) {
            imgData = maybeImg.data;
            imgWidth = maybeImg.width;
            imgHeight = maybeImg.height;
        } else {
            const img = ctx.getImageData(0, 0, W, H);
            imgData = img.data;
            imgWidth = img.width;
            imgHeight = img.height;
        }
        (map as any).addImage(id, { width: imgWidth, height: imgHeight, data: imgData }, { sdf: false, pixelRatio: PIXEL_RATIO });
        (map as any).__assetIconMeta ||= new Map<string, { w: number; h: number; pixelRatio: number }>();
        (map as any).__assetIconBitmap ||= new Map<string, { w: number; h: number; pixelRatio: number; data: Uint8ClampedArray }>();
        (map as any).__assetIconMeta.set(id, { w: imgWidth, h: imgHeight, pixelRatio: PIXEL_RATIO });
        (map as any).__assetIconBitmap.set(id, { w: imgWidth, h: imgHeight, pixelRatio: PIXEL_RATIO, data: imgData });
        cache.add(id);
    };
    const drawRingStyle = (ctx: CanvasRenderingContext2D) => {
        const RING_INNER_R = 5;
        const STROKE_W = 3;
        const RING_OUTER_R = 6;
        ctx.beginPath();
        ctx.arc(0, 0, RING_INNER_R, 0, Math.PI * 2);
        ctx.fillStyle = HEX_WHITE;
        ctx.fill();
        ctx.lineWidth = STROKE_W;
        ctx.strokeStyle = toHex6(divisionColor);
        ctx.beginPath();
        ctx.arc(0, 0, RING_OUTER_R, 0, Math.PI * 2);
        ctx.stroke();
        ctx.save();
        ctx.beginPath();
        ctx.arc(0, 0, RING_OUTER_R + STROKE_W / 2, 0, Math.PI * 2);
        ctx.arc(0, 0, RING_OUTER_R - STROKE_W / 2, 0, Math.PI * 2, true);
        ctx.clip('evenodd');
        ctx.fillStyle = toHex6(divisionColor);
        const pad = STROKE_W;
        ctx.fillRect(-(RING_OUTER_R + pad), 0, RING_OUTER_R + pad, RING_OUTER_R + pad);
        ctx.restore();
        const ARROW_SCALE = 0.7;
        const tipY = -5.0 * ARROW_SCALE;
        const baseY = 3.8 * ARROW_SCALE;
        const halfW = 3.6 * ARROW_SCALE;
        const notchD = 2.2 * ARROW_SCALE;
        ctx.save();
        ctx.rotate((angleBucket * Math.PI) / 180);
        ctx.fillStyle = toHex6(arrowColor);
        ctx.beginPath();
        ctx.moveTo(0, tipY); ctx.lineTo(+halfW, baseY); ctx.lineTo(-halfW, baseY); ctx.closePath();
        ctx.moveTo(-halfW, baseY); ctx.lineTo(0, baseY - notchD); ctx.lineTo(+halfW, baseY); ctx.closePath();
        ctx.fill('evenodd');
        ctx.restore();
        return ctx.getImageData(0, 0, W, H);
    };
    const drawPillStyle = () => {
        const ringInnerR = 5;
        const strokeW = 3;
        const ringOuterR = 6;
        const circleR = ringOuterR + strokeW / 2;
        const PILL_H = 2 * circleR;
        const r = PILL_H / 2;
        const FONT_PX = 8;
        const TEXT_COLOR = '#ffffff';
        const PAD_L = 6;
        const PAD_R = 8;
        const cc = document.createElement('canvas');
        const ctx = cc.getContext('2d', { willReadFrequently: true })!;
        ctx.imageSmoothingEnabled = true;
        ctx.scale(PIXEL_RATIO, PIXEL_RATIO);
        const theFont = `600 ${FONT_PX}px system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif`;
        ctx.font = theFont;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        const text = (label || '').toString();
        const textW = Math.ceil(text ? ctx.measureText(text).width : 0);
        const rightFillFromText = text ? (PAD_L + textW + PAD_R) : 0;
        const forcedGrow = Math.max(0, pillGrowRight || 0);
        const pillWidth = 2 * r + Math.max(rightFillFromText, forcedGrow);
        const PAD = 2;
        const leftExtent = r + PAD;
        const rightExtent = (pillWidth - r) + PAD;
        const canvasCssW = Math.ceil(2 * Math.max(leftExtent, rightExtent));
        const canvasCssH = Math.max(CANVAS_SIZE, Math.ceil(PILL_H + PAD * 2));
        cc.width = Math.ceil(canvasCssW * PIXEL_RATIO);
        cc.height = Math.ceil(canvasCssH * PIXEL_RATIO);
        ctx.setTransform(PIXEL_RATIO, 0, 0, PIXEL_RATIO, cc.width / 2, cc.height / 2);
        const leftX = -r;
        roundedRectPath(ctx, leftX, -PILL_H / 2, pillWidth, PILL_H, r);
        ctx.fillStyle = toHex6(divisionColor);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(0, 0, ringInnerR, 0, Math.PI * 2);
        ctx.fillStyle = '#ffffff';
        ctx.fill();
        ctx.lineWidth = strokeW;
        ctx.strokeStyle = toHex6(divisionColor);
        ctx.beginPath();
        ctx.arc(0, 0, ringOuterR, 0, Math.PI * 2);
        ctx.stroke();
        ctx.save();
        ctx.beginPath();
        ctx.arc(0, 0, ringOuterR + strokeW / 2, 0, Math.PI * 2);
        ctx.arc(0, 0, ringOuterR - strokeW / 2, 0, Math.PI * 2, true);
        ctx.clip('evenodd');
        ctx.fillStyle = toHex6(divisionColor);
        const pad = strokeW;
        ctx.fillRect(-(ringOuterR + pad), 0, ringOuterR + pad, ringOuterR + pad);
        ctx.restore();
        const ARROW_SCALE = 0.7;
        const tipY = -5.0 * ARROW_SCALE;
        const baseY = 3.8 * ARROW_SCALE;
        const halfW = 3.6 * ARROW_SCALE;
        const notchD = 2.2 * ARROW_SCALE;
        ctx.save();
        ctx.rotate((angleBucket * Math.PI) / 180);
        ctx.fillStyle = toHex6(arrowColor);
        ctx.beginPath();
        ctx.moveTo(0, tipY); ctx.lineTo(+halfW, baseY); ctx.lineTo(-halfW, baseY); ctx.closePath();
        ctx.moveTo(-halfW, baseY); ctx.lineTo(0, baseY - notchD); ctx.lineTo(+halfW, baseY); ctx.closePath();
        ctx.fill('evenodd');
        ctx.restore();
        if (text) {
            ctx.font = theFont;
            ctx.textBaseline = 'middle';
            ctx.textAlign = 'left';
            ctx.fillStyle = TEXT_COLOR;
            const textStartX = circleR + PAD_L;
            ctx.fillText(text, textStartX, 0);
        }
        return ctx.getImageData(0, 0, cc.width, cc.height);
    };
    addImageWithMeta(baseId, drawRingStyle);
    addImageWithMeta(pillId, drawPillStyle);
    if (usePill) {
        return pillId;
    }
    return baseId;
};

/**
 * Ensures the snail trail directional marker image has been loaded into the map, if so does nothing
 * @param map current value of your map ref
 */
const loadSnailTrailDirectionalMarkerImage = (
    map: maplibregl.Map,
    imageId: string
) => {
    if ((map as any).hasImage?.(imageId)) {
        return;
    }
    const pixelRatio = 2;
    const diameterImg = 32;
    const strokeImg = 4;
    const chevStrokeImg = 3;
    const chevW = 8 * pixelRatio;
    const chevH = 6 * pixelRatio;
    const c = document.createElement('canvas');
    c.width = diameterImg; c.height = diameterImg;
    const ctx = c.getContext('2d', { willReadFrequently: true })!;
    const cx = diameterImg / 2;
    const cy = diameterImg / 2;
    const r = (diameterImg - strokeImg) / 2;
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.lineWidth = strokeImg;
    ctx.strokeStyle = constants.GOOGLE_PATH_BLUE;
    ctx.stroke();
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.lineWidth = chevStrokeImg;
    ctx.strokeStyle = constants.GOOGLE_PATH_BLUE;
    ctx.beginPath();
    ctx.moveTo(cx - chevW * 0.35, cy - chevH / 2);
    ctx.lineTo(cx + chevW * 0.35, cy);
    ctx.lineTo(cx - chevW * 0.35, cy + chevH / 2);
    ctx.stroke();
    const img = ctx.getImageData(0, 0, c.width, c.height);
    (map as any).addImage(imageId, { width: img.width, height: img.height, data: img.data }, { pixelRatio });
};

/**
 * Ensures the snail trail event icon image has been loaded into the map, if so does nothing
 * @param map current value of your map ref
 */
const loadSnailTrailEventIconImage = (
    map: maplibregl.Map,
    iconImg: string
) => {
    if (!map || map.hasImage(iconImg)) {
        return;
    }
    const notificationSvg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
            <circle cx="16" cy="16" r="14" fill="#ffffff" stroke="LimeGreen" stroke-width="4"/>
            <g transform="translate(16 16) scale(0.7) translate(-16 -16)">
                <path fill="none" stroke="#892f3d" stroke-width="3" stroke-linejoin="round" stroke-linecap="round"
                    d="M16 26a3 3 0 0 0 3-3h-6a3 3 0 0 0 3 3Zm10-5h-1V14a9 9 0 1 0-18 0v7H6 a1 1 0 0 0 0 2h20a1 1 0 0 0 0-2Z"/>
            </g>
        </svg>
    `;
    const img = new Image();
    img.onload = () => {
        try {
            map.addImage(iconImg, img, { pixelRatio: 2 });
        } catch {}
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(notificationSvg)}`;
};

/**
 * Show a ping animation on the given map at the given lng/lat
 * @param map the current value of your map ref
 * @param lng the longitude to ping
 * @param lat the latitude to ping
 */
const showMapPing = (
    map: maplibregl.Map,
    lng: number,
    lat: number
) => {
    const CSS_BLUE = 'var(--blue)' as const;
    const BOX_SHADOW_WHITE = '0 0 0 2px #fff' as const;
    const BOX_SHADOW_BLUE = '0 0 0 6px rgba(26,115,232,0.35)' as const;
    const DURATION_MS = 900 as const;
    const Z_INDEX = 9999 as const;
    const el = document.createElement('div');
    el.style.position = 'relative';
    el.style.width = '14px';
    el.style.height = '14px';
    el.style.borderRadius = '50%';
    el.style.background = CSS_BLUE;
    el.style.boxShadow = `${BOX_SHADOW_WHITE}, ${BOX_SHADOW_BLUE}`;
    el.style.pointerEvents = 'none';
    const ring = document.createElement('div');
    ring.style.position = 'absolute';
    ring.style.inset = '-10px';
    ring.style.borderRadius = '50%';
    ring.style.border = '2px solid var(--blue-dark)';
    ring.style.opacity = '0.6';
    ring.style.transform = 'scale(0.2)';
    el.appendChild(ring);
    const marker = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([lng, lat])
        .addTo(map);
    (marker as any).setZIndex?.(50) ?? (marker.getElement().style.zIndex = '50');
    const DURATION = 900;
    const anim = ring.animate([{ 
        transform: 'scale(0.2)', 
        opacity: 0.6,
    },
    { 
        transform: 'scale(1.8)', 
        opacity: 0.0, 
    }],
    { 
        duration: DURATION, 
        easing: 'ease-out', 
        iterations: Infinity, 
        fill: 'none', 
    });
    const cleanup = () => marker.remove();
    if (anim && 'finished' in anim) {
        (anim as any).finished.then(cleanup).catch(cleanup);
    } else {
        window.setTimeout(cleanup, DURATION_MS * 3 + 50);
    }
};

/**
 * Get IBM Carbon icon component from the given name
 * @param type a valid EventIconType
 */
const getEventIconElement = (type: EventIconType): React.ReactElement => {
    const ICON_SIZE = 12;
    const ICON_COLOR = 'var(--red-dark)';
    const defaultIcon = (
        <NotificationFilled
            size={ICON_SIZE}
            color={ICON_COLOR}
        />
    );
    switch (type) {
        case EventIconType.Alert:
        case EventIconType.Bell: {
            return defaultIcon;
        }
        case 'api': {
            return (
                <PlugFilled
                    size={ICON_SIZE}
                    color={ICON_COLOR}
                />
            );
        }
        case 'environment': {
            return (
                <LocationFilled
                    size={ICON_SIZE}
                    color={ICON_COLOR}
                />
            );
        }
        case 'flag': {
            return (
                <FlagFilled
                    size={ICON_SIZE}
                    color={ICON_COLOR}
                />
            );
        }
        default: {
            return defaultIcon;
        }
    }
}

/**
 * Safely remove a layer from the map
 * @param map the current value of your map ref
 * @param id the id of the layer to remove
 */
const safelyRemoveMapLayer = (
    map: maplibregl.Map,
    id: string
): void => {
    if (map.getLayer(id)) {
        map.removeLayer(id);
    }
};

/**
 * Safely remove a source from the map
 * @param map the current value of your map ref
 * @param id the id of the source to remove
 */
const safelyRemoveMapSource = (
    map: maplibregl.Map,
    id: string
): void => {
    if (map.getSource(id)) {
        map.removeSource(id as any);
    }
};

/**
 * Safely remove an image from the map
 * @param map the current value of your map ref
 * @param id the id of the image to remove
 */
const safelyRemoveMapImage = (
    map: maplibregl.Map,
    id: string
): void => {
    if (map.getImage(id)) {
        map.removeImage(id as any);
    }
};

export {
    mapHeatMap, mapHeatMapMarkers, setMarkerPopoverToLoading, requestMarkerDetails, createFeatureCollectionFromAssetMarkerData,
    setMarkerPopoverDetails, getMapAssetPopupHead, getMapAssetPopupContent, calculateSpeedAndUnit, safelyInteractWithMap,
    isValidLngLat, createFeatureCollectionFromSnailTrailPoints, getLngLatFromSnailTrailPoint, mergeAssetMarkers, loadAssetMarker,
    getEventIconElement, createFeatureCollectionFromGeofences, createFeatureCollectionFromHeatmapEvents,
    showMapPing, pickTopmostHitAlpha, getDeviceStatusColor, getMapStyle, loadSnailTrailDirectionalMarkerImage,
    loadSnailTrailEventIconImage, getSnailTrailDirectionalArrowSpacingForZoom, safelyRemoveMapLayer, safelyRemoveMapImage, 
    safelyRemoveMapSource, getBoundsForGeojson, createGeofenceCentersFromGeojson,
};
